import React from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { z } from "zod";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { HOTKEYS_SCOPES } from "@hooks/keyboard/useKeyboardCommand";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";

const addReceiptSenderFormSchema = z.object({
    email: z
        .string()
        .min(1, "Email is required")
        .email("Please provide a valid email address"),
});

type AddReceiptSenderFormValues = z.infer<typeof addReceiptSenderFormSchema>;

const fields = createFormikFields<AddReceiptSenderFormValues>();

interface AddReceiptSenderFormProps {
    onSubmit: (email: string) => void;
    onCancel: () => void;
}

export const AddReceiptSenderForm: React.FC<AddReceiptSenderFormProps> = ({
    onSubmit,
    onCancel,
}) => {
    const formik = useFormik<AddReceiptSenderFormValues>({
        initialValues: {
            email: "",
        },
        validate: validateFormikWithZodSchema(addReceiptSenderFormSchema),
        onSubmit: submitHelper({
            handler: async ({ email }) => {
                onSubmit(email.trim());
            },
        }),
    });

    return (
        <FormikProvider value={formik}>
            <Form>
                <VStack gap={16}>
                    <fields.Text
                        name="email"
                        type="email"
                        label="Email Address"
                        placeholder="sender@example.com"
                        size="xl"
                    />
                    <div>
                        <p className="text-muted text-small">
                            We'll send a verification code to this email address
                            to confirm ownership.
                        </p>

                        <HStack as="footer" justify="end" gap={12}>
                            <Button
                                variant="secondary"
                                size="xl"
                                type="button"
                                onClick={onCancel}
                                disabled={formik.isSubmitting}
                            >
                                Cancel
                            </Button>

                            <ButtonWithLoader
                                variant="primary"
                                size="xl"
                                disabled={!formik.isValid}
                                loading={formik.isSubmitting}
                                type="submit"
                                hotKey="mod+enter"
                                hotKeyOptions={{
                                    description: "Add email address",
                                    preventDefault: true,
                                    enableOnFormTags: true,
                                    scopes: [HOTKEYS_SCOPES.SETTINGS_MODAL],
                                }}
                            >
                                Next
                            </ButtonWithLoader>
                        </HStack>
                    </div>
                </VStack>
            </Form>
        </FormikProvider>
    );
};
